import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Badge,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Paper
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Login as LoginIcon,
  Receipt as ReceiptIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { logoutUser } from '../../services/authService';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';
import CartDrawer from './CartDrawer';
import MegaMenu from './MegaMenu';
import LiveSearch from './LiveSearch';



interface StoreHeaderProps {
  onSearch?: (query: string) => void;
  onMobileMenuOpen?: () => void;
}

const StoreHeader: React.FC<StoreHeaderProps> = ({ onSearch, onMobileMenuOpen }) => {
  const { cart } = useCart();
  const { currentUser, setCurrentUser } = useAuth();
  const { settings } = useStoreSettings();
  const [cartOpen, setCartOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // استخراج اسم المتجر واللوجو من الإعدادات
  const storeName = settings?.general?.storeName || 'اليكترونيات السواح';
  const storeLogo = settings?.general?.storeLogo;

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      setCurrentUser(null);
      handleUserMenuClose();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <>
      {/* حقل البحث في أعلى الهيدر */}
      <Paper 
        elevation={1} 
        sx={{ 
          width: '100%', 
          backgroundColor: 'background.paper',
          py: 1.5,
          display: 'flex',
          justifyContent: 'center',
          borderBottom: '1px solid', 
          borderColor: 'divider'
        }}
      >
        <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%'
            }}
          >
            <LiveSearch
              onSearch={onSearch}
              placeholder="بحث عن منتجات..."
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: '80%', md: '60%', lg: '50%' }
              }}
            />
          </Box>
        </Container>
      </Paper>

      <AppBar position="static" color="primary" elevation={0} className="store-header">
        <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
          <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between' }}>
            {/* القسم الأيمن: اسم المتجر وأيقونة القائمة المتنقلة */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isMobile && (
                <IconButton
                  size="large"
                  edge="start"
                  color="inherit"
                  aria-label="menu"
                  sx={{ mr: 2 }}
                  onClick={onMobileMenuOpen}
                >
                  <MenuIcon />
                </IconButton>
              )}

              {storeLogo ? (
                <Box
                  component={RouterLink}
                  to="/"
                  sx={{
                    display: { xs: 'flex', md: 'flex' },
                    alignItems: 'center',
                    textDecoration: 'none',
                  }}
                >
                  <Box
                    component="img"
                    src={storeLogo}
                    alt={storeName}
                    sx={{
                      height: { xs: 32, md: 40 },
                      mr: 1
                    }}
                  />
                </Box>
              ) : (
                <Typography
                  variant="h6"
                  noWrap
                  component={RouterLink}
                  to="/"
                  sx={{
                    display: { xs: 'flex', md: 'flex' },
                    fontWeight: 700,
                    color: 'inherit',
                    textDecoration: 'none',
                  }}
                >
                  {storeName}
                </Typography>
              )}
            </Box>

            {/* القسم الأوسط: قائمة المنتجات والأزرار */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, height: '100%' }}>
              {/* Mega Menu للمنتجات */}
              <MegaMenu onCategorySelect={(categoryId) => navigate(`/category/${categoryId}`)} />

              <Button
                component={RouterLink}
                to="/categories"
                sx={{ my: 2, color: 'white', display: 'block' }}
              >
                جميع الفئات
              </Button>

              <Button
                component={RouterLink}
                to="/offers"
                sx={{ my: 2, color: 'white', display: 'block' }}
              >
                العروض
              </Button>
              {!currentUser && (
                <Button
                  component={RouterLink}
                  to="/login"
                  sx={{ my: 2, color: 'white', display: 'block' }}
                  startIcon={<LoginIcon />}
                >
                  تسجيل الدخول
                </Button>
              )}
            </Box>

            <Box sx={{ flexGrow: { xs: 1, md: 0 } }} />

            <Box sx={{ display: 'flex' }}>
              <IconButton
                size="large"
                aria-label="الصفحة الرئيسية"
                color="inherit"
                component={RouterLink}
                to="/"
                title="الصفحة الرئيسية"
                sx={{
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    color: 'white'
                  }
                }}
              >
                <HomeIcon />
              </IconButton>

              {currentUser && (
                <IconButton
                  size="large"
                  aria-label="طلباتي"
                  color="inherit"
                  component={RouterLink}
                  to="/profile"
                  title="طلباتي"
                >
                  <ReceiptIcon />
                </IconButton>
              )}

              <IconButton
                size="large"
                aria-label="عرض سلة التسوق"
                color="inherit"
                onClick={() => setCartOpen(true)}
              >
                <Badge badgeContent={cart.totalItems} color="error">
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>

              <IconButton
                size="large"
                aria-label="حساب المستخدم"
                color="inherit"
                onClick={handleUserMenuOpen}
              >
                <PersonIcon />
              </IconButton>
              <Menu
                id="user-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleUserMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                {currentUser ? (
                  <>
                    <MenuItem component={RouterLink} to="/profile" onClick={handleUserMenuClose}>
                      <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                      الملف الشخصي
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                      تسجيل الخروج
                    </MenuItem>
                  </>
                ) : (
                  <MenuItem component={RouterLink} to="/login" onClick={handleUserMenuClose}>
                    <LoginIcon fontSize="small" sx={{ mr: 1 }} />
                    تسجيل الدخول
                  </MenuItem>
                )}
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
      />
    </>
  );
};

export default StoreHeader;
